import { Component, ElementRef, inject, ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { MenuItem } from '@openng/optimus-ui/api';
import { Tree } from '@openng/optimus-ui/tree';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { ChangeSummary } from 'src/modules/enaio/netgen/ChangeSummary';
import { ToolTipDefinition } from 'src/modules/sm-base/models/ToolTipDefinition.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableCellEvent } from 'src/modules/sm-base/models/table-cell-event.model';
import { TableCell } from 'src/modules/sm-base/models/table-cell.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { TreeSelectionHelper } from 'src/modules/utils/misc/tree-selection-helper';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Dates } from 'src/modules/utils/shared/dates';
import { Objects } from 'src/modules/utils/shared/objects';
import { Strings } from 'src/modules/utils/shared/strings';
import { Utils } from 'src/modules/utils/shared/utils';
import { FullCalendarWrapper } from '../../models/full-calendar-wrapper.model';
import { ProjectWorkspaceSummary } from "../../models/project-workspace-summary.model";
import { CalendarEvent } from '../../netgen/CalendarEvent';
import { CalendarEventKind } from '../../netgen/CalendarEventKind';
import { Resource } from '../../netgen/Resource';
import { ProjectPlanningService } from '../../services/project-planning.service';
import { CalendarEventToolTipComponent } from '../calendar-event-tool-tip/calendar-event-tool-tip.component';

@Component({
    selector: 'app-planning-view-my-workspace',
    templateUrl: './planning-view-my-workspace.component.html',
    styleUrl: './planning-view-my-workspace.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PlanningViewMyWorkspaceComponent extends ComponentView {

    service = inject(ProjectPlanningService);

    viewOptions = FrontendFieldListItem.make(["table", "week", "month"], item => [item, this.app.t("ProjectPlanning.view_" + item)], false);
    selectedView = "table";
    table: TableData;
    treeData: TreeSelectionHelper;
    form: FrontendForm;
    displayedCalendarIds: number[];
    events: CalendarEvent[];
    resources: Resource[];
    filteredEvents: CalendarEvent[];
    calendarWrapper: FullCalendarWrapper;
    summary = new ProjectWorkspaceSummary();

    @ViewChild("fc") 
    fc: FullCalendarComponent;
    @ViewChild("treeElement") 
    tree: Tree;
    @ViewChild("treeElement", { read: ElementRef }) 
    treeElement: ElementRef;

    async initParams(): Promise<boolean> {
        this.resources = await this.service.getResources();
        await this.service.getOutlineLevels();

        this.calendarWrapper = new FullCalendarWrapper(false, false, "none", "calendar", this.calendarsChanged.bind(this), this, this.app, this.service);

        this.treeData = new TreeSelectionHelper(this.tree, false);

        await this.updateTree();

        let userId = this.service.getCurrentUser().id;
        let responsibleForRoles = (await this.service.getRoles()).filter(r => r.responsibleId == userId);
        this.treeData.setSelected(["21" + userId, ...responsibleForRoles.map(r => "22" + r.id)]);
        if (!Utils.isNoe(responsibleForRoles)) {
            this.treeData.getNode("02").expanded = true;
        }

        this.form = new FrontendForm([
            new FrontendField("showDeliveryDates", this.app.t("ProjectPlanning.showDeliveryDates"), FrontendFieldType.bool, {
                value: false,
                onValueChanged: this.updateTable.bind(this)
            }),
            new FrontendField("statusActive", this.app.t("ProjectPlanning.active"), FrontendFieldType.bool, { 
                value: true,
                onValueChanged: this.updateTableAndCalendar.bind(this)
            }),
            new FrontendField("statusFinished", this.app.t("ProjectPlanning.finished"), FrontendFieldType.bool, { 
                onValueChanged: this.updateTableAndCalendar.bind(this)
            }),
            new FrontendField("past", this.app.t("ProjectPlanning.showHistory"), FrontendFieldType.dropdown, {
                value: 1,
                listItems: FrontendFieldListItem.make([1, 6, 12, null], item => [item, item == null ? this.app.t("ProjectPlanning.all") : this.app.t("ProjectPlanning.lastMonths", { months: item })], false),
                onValueChanged: this.updateTableAndCalendar.bind(this)
            })
        ]);
        this.updateIsMobile();

        await this.calendarsChangedInternal();

        this.addSubscription(this.app.registerChangesCallback(this.serviceChanged.bind(this), "PlanningViewMyWorkspace"));
        this.addSubscription(this.app.onIsMobileChanged.subscribe(() => this.updateIsMobile()));

        return true;
    }

    updateIsMobile(): void {
        this.form.getField("showDeliveryDates").guiGroup = this.app.isMobile() ? "1" : null;
        this.form.getField("statusActive").guiGroup = this.app.isMobile() ? "1" : null;
        this.form.getField("statusFinished").guiGroup = this.app.isMobile() ? "1" : null;
        this.form.getField("showDeliveryDates").guiGroupInline = this.app.isMobile();
        this.form.getField("statusActive").guiGroupInline = this.app.isMobile();
        this.form.getField("statusFinished").guiGroupInline = this.app.isMobile();
        this.form = new FrontendForm(this.form.fields);
    }

    async serviceChanged(summary: ChangeSummary): Promise<void> {
        if (summary.hasAnyType(["CalendarRole", "ResourceType", "DatabaseUser", "DatabaseUserGroup", "Company"])) {
            await this.updateTree();
            GuiUtils.updateTreeSelectedColors(this.treeData.items, this.treeData.getSelectedNodes(), this.treeElement?.nativeElement as HTMLElement);
        }   
        if (summary.hasAnyType(["CalendarEvent", "CalendarEventRole"])) {
            await this.calendarsChangedInternal();
        }           
    }

    async updateTree(): Promise<void> {
        this.treeData.setItems(await this.service.getGuiTree([]));
    }

    getTableColumns(): TableColumn[] {
        let anySubTasks = this.filteredEvents?.some(e => !Utils.isNoe(e.subTasks)); 
        return [
            this.service.getTableColumn("kind"),
            this.service.getTableColumn("userName"),
            this.service.getTableColumn("title").with({
                cellRenderer: evt => {
                    let ce = Objects.castOrNull((evt.data as TableRow).raw, CalendarEvent);
                    if (ce == null) {
                        return evt.value;
                    }
                    return "<span>" + evt.value + "</span>"
                    + (ce.isPrivate ? "<i class=\"ml-2 fas fa-lock font-size-075rem\" title=\"\">" : "");
                }
            }),
            anySubTasks ? this.service.getTableColumn("subTasks") : null,
            this.service.getTableColumn("start"),
            this.service.getTableColumn("end"),
            this.service.getTableColumn("plannedTime"),  
            this.service.getTableColumn("doneTime"),  
            new TableColumn("resources", this.app.t("entity.CalendarEvent.fields.resources"), TableCellType.string, 20),
            new TableColumn("projectNumber", this.app.t("entity.Project.fields.projectNumber"), TableCellType.string, 14),
            new TableColumn("projectName", this.app.t("ProjectPlanning.projectTitle"), TableCellType.string, 30),
            new TableColumn("structurePath1", this.app.t("ProjectPlanning.outlineLevelWithIndex", { index: 1 }), TableCellType.string, 20)
        ];
    }

    viewChanged(): void {
        if (this.selectedView == "week") {
            GuiUtils.angularTimer(() => this.fc.getApi().changeView('timeGridWeek'));
        }
        else if (this.selectedView == "month") {
            GuiUtils.angularTimer(() => this.fc.getApi().changeView('dayGridMonth'));
        }
        this.calendarsChanged();
    }

    debouncedCalendarsChanged = Utils.debounce(async () => this.calendarsChangedInternal(), 50);

    calendarsChanged(): void {
        this.debouncedCalendarsChanged();
    }

    async calendarsChangedInternal(): Promise<void> {        
        this.displayedCalendarIds = Arrays.withoutNull(this.treeData.getSelectedNodes().map(sel => sel.data?.calendarId as number));
        this.events = await this.service.getEventsForCalendars(this.displayedCalendarIds);

        this.updateSummary();

        GuiUtils.updateTreeSelectedColors(this.treeData.items, this.treeData.getSelectedNodes(), this.treeElement?.nativeElement as HTMLElement);
        await this.updateTableAndCalendar();
    }

    updateSummary(): void {
        let today = Dates.startOf(new Date(), "day");
        let tomorrow = Dates.add(today, "day", 1);
        let openTasks = this.events.filter(event => event.kind != CalendarEventKind.appointment && !event.finished);
        let appointments = this.events.filter(event => event.kind == CalendarEventKind.appointment);

        this.summary = {
            overdueCount: openTasks.filter(event => event.startDate < today).length,
            todayTasksCount: openTasks.filter(event =>
                    event.startDate >= today && event.startDate < tomorrow
            ).length,
            todayAppointmentsCount: appointments.filter(event =>
                    event.startDate >= today && event.startDate < tomorrow
            ).length
        };
    }

    async updateTableAndCalendar(): Promise<void> {
        await this.updateTable();
        await this.initCalendar();
    }

    async updateTable(): Promise<void> {
        let statusActive = this.form.getValueBool("statusActive");
        let statusFinished = this.form.getValueBool("statusFinished");
        this.form.getField("past").visible = statusFinished;
        let pastMonths = this.form.getValueNumber("past");
        let showDeliveryDates = this.form.getValueBool("showDeliveryDates");
        let startDate = pastMonths != null ? Dates.add(Dates.startOf(new Date(), "day"), "month", -pastMonths) : null;

        this.filteredEvents = this.events.filter(e => e.isFinished() ? statusFinished && (startDate == null || startDate <= e.startDate) : statusActive);
        let columns = this.getTableColumns();
        let now = new Date();
        let nowDate = Dates.withoutTime(now);
        let projects = await this.service.getProjects();
        let rows = this.filteredEvents.map(e => new TableRow(Objects.toString(e.id), e as any, {
            kind: new TableCell(CalendarEventKind.getDisplayShortName(e.kind, this.app), { styleClass: CalendarEventKind.getStyleClass(e.kind) }),
            userName: e.getAllEditorsText(this.service, true),
            title: new TableCell(e.title, { toolTip: new ToolTipDefinition(CalendarEventToolTipComponent, { event: e })}),
            subTasks: Utils.isNoe(e.subTasks) ? "" : e.subTasks.filter(st => st.finished).length + "/" + e.subTasks.length,
            start: e.startDate,
            end: new TableCell(e.getDisplayEndDate(), { styleClass: e.endDate < nowDate && !e.isFinished() ? "xbg-red-300" : e.isFinished() ? "xbg-gray-400" : "" }),
            plannedTime: e.plannedTime,
            doneTime: new TableCell(e.doneTime, { styleClass: e.doneTime > e.plannedTime ? " xbg-red-300" : ""}),
            resources: Arrays.itemsToString(Arrays.sort(e.resources ?? []).map(r => this.resources.find(r2 => r2.id == r.resourceId)?.title ?? "<???>")),
            projectNumber: e.kind == CalendarEventKind.appointment ? "" : projects.find(p => p.id == e.projectId)?.projectNumber ?? "<???>",
            projectName: e.kind == CalendarEventKind.appointment ? "" : projects.find(p => p.id == e.projectId)?.title ?? "<???>",
            structurePath1: this.service.formatStructurePath(Strings.getSubstringTo(e.structurePath ?? "", "|", true))
        }, {
            getContextMenuItems: this.getContextMenuItems.bind(this)
        }));

        if (showDeliveryDates) {
            let seenProjects = new Set<number>();
            for (let event of this.filteredEvents) {
                let project = projects.find(p => p.id == event.projectId);
                if (project != null && !seenProjects.has(project.id)) {
                    seenProjects.add(project.id);
                    for (let i = 0; i < [project.deliveryDate1, project.deliveryDate2, project.deliveryDate3].length; i++) {
                        let deliveryDate = [project.deliveryDate1, project.deliveryDate2, project.deliveryDate3][i];
                        if (deliveryDate != null) {
                            rows.push(new TableRow("delivery-" + project.id + "-" + i, { project, deliveryDate, index: i + 1 } as any, {
                                kind: new TableCell(this.app.t("ProjectPlanning.deliveryDate"), { styleClass: "xbg-green-300" }),
                                title: new TableCell(this.app.t("ProjectPlanning.deliveryDateWithIndex", { index: i + 1 }), { styleClass: "xbg-green-300" }),
                                projectNumber: project.projectNumber ?? "",
                                projectName: project.title ?? "",
                                start: deliveryDate.toDate(),
                                end: deliveryDate.toDate()
                            }, {
                                styleClass: "xbg-green-300",
                                getContextMenuItems: () => []
                            }));
                        }
                    }
                }
            }
        }

        rows.push(new TableRow("", null as any, {
            structurePath1: new TableCell(this.app.t("ProjectPlanning.todayUpper"), { colSpan: -1 }),
            start: now,
            end: now
        }, { virtual: true, height: 3, styleClass: "xbg-red-300"}));

        this.table = TableData.update(this.table, columns, rows, ["start"]);
    }

    async initCalendar(): Promise<void> {
        await this.calendarWrapper.initCalendar(() => this.fc, this.filteredEvents, [], this.displayedCalendarIds); 
    }  

    async cellEdited(event: TableCellEvent): Promise<void> {
        if (event.column.colId == "doneTime" && event.row.raw instanceof CalendarEvent) {
            let evt = event.row.raw;
            await RestEndpoint.main().query({id: evt.id, doneTime: evt.doneTime}).post().run("api/projectplanning/calendarEvent/setDoneTime").getText();
            await this.calendarsChangedInternal();
        }
    }

    getContextMenuItems(e: TableCellEvent): MenuItem[] {
        return e.row.raw instanceof CalendarEvent ? this.service.getEventContextMenuItems(e.row.raw, this.service.getCurrentUser(), this.calendarsChangedInternal.bind(this), this) : [];
    }

    async eventClicked(row: TableRow): Promise<void> {
        if (row.raw instanceof CalendarEvent) {
            await this.service.editEvent(row.raw, this.calendarsChangedInternal.bind(this));
        }
    }
}
